import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getPayments,
  createPayment,
  getPaymentDepositQuotes,
  sharePayment,
  regeneratePaymentLink,
  getPaymentDetails,
  getSecurePaymentDetails,
  deletePayment,
} from "./thunk";
import {
  PaymentListPayload,
  CreatePaymentPayload,
  PaymentDepositQuotePayload,
  PaymentDetailsPayload,
  SecurePaymentDetailsPayload,
  LinkPayload,
} from "types/payment.api";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "types/ApiResponse";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface PaymentStatus {
  fetch: RequestStatus;
  add: RequestStatus;
  fetchDepositQuotes: RequestStatus;
  share: RequestStatus;
  regenerateLink: RequestStatus;
  fetchDetails: RequestStatus;
  delete: RequestStatus;
}

interface PaymentErrors {
  fetch: string | null;
  add: string | null;
  fetchDepositQuotes: string | null;
  share: string | null;
  regenerateLink: string | null;
  fetchDetails: string | null;
  delete: string | null;
}

export interface PaymentState {
  payments: PaymentListPayload[];
  depositQuotes: PaymentDepositQuotePayload[];
  paymentDetails: PaymentDetailsPayload | null;
  securePaymentDetails: SecurePaymentDetailsPayload | null;
  pagination: PaginationMeta;
  status: PaymentStatus;
  errors: PaymentErrors;
  generatedLinkDetails: LinkPayload | null;
}

export const initialState: PaymentState = {
  payments: [],
  depositQuotes: [],
  paymentDetails: null,
  securePaymentDetails: null,
  generatedLinkDetails: null,
  pagination: {
    current_page: 0,
    from: 0,
    last_page: 0,
    links: [],
    path: "",
    per_page: 0,
    to: 0,
    total: 0,
  },
  status: {
    fetch: "idle",
    add: "idle",
    fetchDepositQuotes: "idle",
    share: "idle",
    regenerateLink: "idle",
    fetchDetails: "idle",
    delete: "idle",
  },
  errors: {
    fetch: null,
    add: null,
    fetchDepositQuotes: null,
    share: null,
    regenerateLink: null,
    fetchDetails: null,
    delete: null,
  },
};

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    resetCreatePaymentStatus(state) {
      state.status.add = "idle";
    },
    clearPaymentDetails(state) {
      state.paymentDetails = null;
      state.securePaymentDetails = null;
    },
  },
  extraReducers: (builder) => {
    // get Payments
    builder.addCase(getPayments.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
    });
    builder.addCase(
      getPayments.fulfilled,
      (
        state,
        action: PayloadAction<
          ApiResponse<PaginatedPayload<PaymentListPayload>>
        >,
      ) => {
        state.status.fetch = "succeeded";
        const responsePayload = action.payload?.payload;
        state.payments = responsePayload?.data || [];
        state.pagination = responsePayload?.meta || initialState.pagination;
      },
    );
    builder.addCase(getPayments.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
    });

    // create payment
    builder.addCase(createPayment.pending, (state) => {
      state.status.add = "loading";
      state.errors.add = null;
    });
    builder.addCase(
      createPayment.fulfilled,
      (state, action: PayloadAction<ApiResponse<CreatePaymentPayload>>) => {
        state.status.add = "succeeded";
        state.pagination.to++;
        state.pagination.total++;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page,
        );
      },
    );
    builder.addCase(createPayment.rejected, (state, action) => {
      state.status.add = "failed";
      state.errors.add = action.payload || null;
    });

    // get payment deposit quotes
    builder.addCase(getPaymentDepositQuotes.pending, (state) => {
      state.status.fetchDepositQuotes = "loading";
      state.errors.fetchDepositQuotes = null;
    });
    builder.addCase(
      getPaymentDepositQuotes.fulfilled,
      (
        state,
        action: PayloadAction<
          ApiResponse<PaginatedPayload<PaymentDepositQuotePayload>>
        >,
      ) => {
        state.status.fetchDepositQuotes = "succeeded";
        state.depositQuotes = action.payload?.payload?.data || [];
      },
    );
    builder.addCase(getPaymentDepositQuotes.rejected, (state, action) => {
      state.status.fetchDepositQuotes = "failed";
      state.errors.fetchDepositQuotes = action.payload || null;
      state.depositQuotes = [];
    });

    // share payment
    builder.addCase(sharePayment.pending, (state) => {
      state.status.share = "loading";
      state.errors.share = null;
    });
    builder.addCase(sharePayment.fulfilled, (state) => {
      state.status.share = "succeeded";
    });
    builder.addCase(sharePayment.rejected, (state, action) => {
      state.status.share = "failed";
      state.errors.share = action.payload || null;
    });

    // regenerate payment link
    builder.addCase(regeneratePaymentLink.pending, (state) => {
      state.status.regenerateLink = "loading";
      state.errors.regenerateLink = null;
    });
    builder.addCase(
      regeneratePaymentLink.fulfilled,
      (state, action: PayloadAction<ApiResponse<LinkPayload>>) => {
        state.status.regenerateLink = "succeeded";
        state.generatedLinkDetails = action.payload.payload;
      },
    );
    builder.addCase(regeneratePaymentLink.rejected, (state, action) => {
      state.status.regenerateLink = "failed";
      state.errors.regenerateLink = action.payload || null;
    });

    // get payment details
    builder.addCase(getPaymentDetails.pending, (state) => {
      state.status.fetchDetails = "loading";
      state.errors.fetchDetails = null;
    });
    builder.addCase(
      getPaymentDetails.fulfilled,
      (state, action: PayloadAction<ApiResponse<PaymentDetailsPayload>>) => {
        state.status.fetchDetails = "succeeded";
        state.paymentDetails = action.payload.payload;
      },
    );
    builder.addCase(getPaymentDetails.rejected, (state, action) => {
      state.status.fetchDetails = "failed";
      state.errors.fetchDetails = action.payload || null;
    });

    // get secure payment details
    builder.addCase(getSecurePaymentDetails.pending, (state) => {
      state.status.fetchDetails = "loading";
      state.errors.fetchDetails = null;
    });
    builder.addCase(
      getSecurePaymentDetails.fulfilled,
      (state, action: PayloadAction<ApiResponse<SecurePaymentDetailsPayload>>) => {
        state.status.fetchDetails = "succeeded";
        state.securePaymentDetails = action.payload.payload;
      },
    );
    builder.addCase(getSecurePaymentDetails.rejected, (state, action) => {
      state.status.fetchDetails = "failed";
      state.errors.fetchDetails = action.payload || null;
    });

    // delete payment
    builder.addCase(deletePayment.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deletePayment.fulfilled,
      (state, action) => {
        state.status.delete = "succeeded";
        const deletedId = action.meta.arg.id;
        state.payments = state.payments.filter((p) => p.id !== deletedId);
        state.pagination.total--;
        state.pagination.to = Math.max(0, state.pagination.to - 1);
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page,
        );
      },
    );
    builder.addCase(deletePayment.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });
  },
});

export const { resetCreatePaymentStatus, clearPaymentDetails } =
  paymentSlice.actions;
export default paymentSlice.reducer;
